import { Box } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { EqualizerIcon, OpenInNewIcon, SettingsIcon } from "assets/icons";
import { TooltipModalButton } from "components/common/TooltipModalButton";
import { contentGroundTruthTooltip } from "components/detector/LineChartMetrics/ContentStrings";
import { PipelineDetailsModal } from "components/detector/PipelineDetailsModal/PipelineDetailsModal";
import { useAppSelector } from "hooks/store";
import { GroundlightButton } from "new-components/buttons/GroundlightButton";
import { Typography } from "new-components/typography/Typography";
import { getUserState } from "store/slices/Auth/authSlice";
import { DetectorModes } from "types/createDetector";
import { Detector, EvaluationResults } from "types/detectors";

import AccuracyTimestamp from "./AccuracyTimestamp";
import ProjectedAccuracyMetric from "./ProjectedAccuracyMetric";
import { ProjectedAccuracyModal, getAccuracyByThreshold } from "./ProjectedAccuracyModal";

const REQUIRED_GROUND_TRUTHS_PER_CLASS_IN_BINARY = 4;
const REQUIRED_GROUND_TRUTHS_PER_CLASS_IN_MULTICHOICE = 2;

// This component renders the actions that are displayed at the right side of the ProjectedAccuracy component.
function RenderActions({
  mlAccuracyTimestamp,
  openAccuracyDetailsModal,
  totalQueries,
  evaluationResults,
  detectorMode,
  classNames = [],
  groundTruths,
  lowestGroundTruthClassCount,
  showPipelineDetailsModal,
  openPipelineDetailsModal,
}: {
  mlAccuracyTimestamp?: string;
  openAccuracyDetailsModal: () => void;
  groundTruths: {
    yes: number;
    no: number;
  };
  totalQueries: number;
  evaluationResults: EvaluationResults | null;
  detectorMode: DetectorModes;
  classNames: string[];
  lowestGroundTruthClassCount: number;
  showPipelineDetailsModal: boolean;
  openPipelineDetailsModal: () => void;
}) {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();

  const navigateToDetectorReview = () => navigate(`/reef/review/queue?detector_id=${id}&ground_truth=true`);

  // Data just for binary mode
  const currentPassCount = groundTruths?.yes || 0;
  const currentFailCount = groundTruths?.no || 0;
  const totalGroundTruths = currentPassCount + currentFailCount;
  const missingPassCount = Math.max(0, REQUIRED_GROUND_TRUTHS_PER_CLASS_IN_BINARY - currentPassCount);
  const missingFailCount = Math.max(0, REQUIRED_GROUND_TRUTHS_PER_CLASS_IN_BINARY - currentFailCount);
  // this works just for binary mode.
  const hasGroundTruthForAllQueries = totalQueries > 0 && totalGroundTruths === totalQueries;

  // Data for multichoice mode or binary mode
  const hasEnoughImageQueries = detectorMode === DetectorModes.MULTICHOICE
    ? totalQueries >= classNames.length * REQUIRED_GROUND_TRUTHS_PER_CLASS_IN_MULTICHOICE
    : totalQueries >= 2 * REQUIRED_GROUND_TRUTHS_PER_CLASS_IN_BINARY;

  const hasEnoughGroundTruths = detectorMode === DetectorModes.MULTICHOICE
    ? lowestGroundTruthClassCount >= REQUIRED_GROUND_TRUTHS_PER_CLASS_IN_MULTICHOICE
    : (
      currentPassCount >= REQUIRED_GROUND_TRUTHS_PER_CLASS_IN_BINARY
      && currentFailCount >= REQUIRED_GROUND_TRUTHS_PER_CLASS_IN_BINARY
    );

  // Enter here if evaluation results are available.
  // This is the most common case in old detectors.
  if (evaluationResults) {
    return (
      <>
        {hasGroundTruthForAllQueries ? (
          <Box display="flex" flexDirection="column" justifyContent="end" alignItems="end">
            <Typography variant="detail" className="text-white text-end">
              100% of image queries have ground truth answers.
            </Typography>
            <AccuracyTimestamp mlAccuracyTimestamp={mlAccuracyTimestamp} />
          </Box>
        ) : (
          <AccuracyTimestamp mlAccuracyTimestamp={mlAccuracyTimestamp} />
        )}

        <Box flexDirection="column" className="flex items-center gap-[5px]">
          {!hasGroundTruthForAllQueries && (
            <GroundlightButton onClick={navigateToDetectorReview} endIcon={OpenInNewIcon} size="small">
              Label image queries
            </GroundlightButton>
          )}

          <GroundlightButton onClick={openAccuracyDetailsModal} endIcon={EqualizerIcon} size="small">
            Performance details
          </GroundlightButton>

          {showPipelineDetailsModal && (
            <GroundlightButton onClick={openPipelineDetailsModal} endIcon={SettingsIcon} size="small">
              Pipeline Details
            </GroundlightButton>
          )}
        </Box>
      </>
    );
  }

  if (totalQueries === 0) {
    return (
      <>
        <Typography variant="detail" className="text-white text-end">
          Detector has <b>0</b> Image queries. <br />
          {detectorMode === DetectorModes.MULTICHOICE ? (
            <>
              <b>Needs {REQUIRED_GROUND_TRUTHS_PER_CLASS_IN_MULTICHOICE} ground truth answers per class</b>{" "}
              to estimate performance.
            </>
          ) : (
            <>
              <b>Needs {missingPassCount} Yes and {missingFailCount}No</b>{" "}
              ground truth answers to estimate performance.
            </>
          )}
        </Typography>
        <TooltipModalButton modalTitle="Ground Truth Needed" modalContent={contentGroundTruthTooltip} />
      </>
    );
  }

  // Enter here if there are less image queries than the required GTs so user need not only to label 
  // more image queries but also send more image queries.
  if (!hasEnoughImageQueries && !hasEnoughGroundTruths) {
    return (
      <>
        <Typography variant="detail" className="text-white text-end">
          Detector does not have enough image queries to estimate performance.<br />
          {detectorMode === DetectorModes.MULTICHOICE ? (
            <b>Needs {REQUIRED_GROUND_TRUTHS_PER_CLASS_IN_MULTICHOICE} ground truth answers per class</b>
          ) : (
            <>
              <b>Needs {missingPassCount} Yes and {missingFailCount}No </b>{" "}
                ground truth answers to estimate performance.
            </>
          )}
        </Typography>
        <TooltipModalButton modalTitle="Ground Truth Needed" modalContent={contentGroundTruthTooltip} />
        {!hasGroundTruthForAllQueries && (
          <GroundlightButton onClick={navigateToDetectorReview} endIcon={OpenInNewIcon} size="small">
            Label image queries
          </GroundlightButton>
        )}
      </>
    );
  }

  // Enter here if already have all the image queries needed but not enough ground truths.
  if (hasEnoughImageQueries && !hasEnoughGroundTruths) {
    return (
      <>
        <Typography variant="detail" className="text-white text-end">
          {detectorMode === DetectorModes.MULTICHOICE ? (
            <>
              Detector{" "}
              <b>needs {REQUIRED_GROUND_TRUTHS_PER_CLASS_IN_MULTICHOICE} ground truth answers per class</b> to{" "}
              estimate performance.
            </>
          ) : (
            <>
                Need <b>{missingPassCount}</b> Yes and <b>{missingFailCount}</b> No ground truth answers{" "}
                to estimate performance.
            </>
          )}
        </Typography>
        <TooltipModalButton modalTitle="Ground Truth Needed" modalContent={contentGroundTruthTooltip} />
        <GroundlightButton onClick={navigateToDetectorReview} endIcon={OpenInNewIcon} size="small">
          Label image queries
        </GroundlightButton>
      </>
    );
  }

  // Enter here if already have all the ground truths needed but evaluation results are not available yet.
  // This is temporary state until the evaluation results are available.
  return (
    <>
      <Typography variant="detail" className="text-white text-end">
        Evaluating results. <br />
        {hasGroundTruthForAllQueries
          ? "100% of image queries have Ground Truth labels."
          : "You can still continue adding ground truth labels."}
      </Typography>
      {!hasGroundTruthForAllQueries && (
        <GroundlightButton onClick={navigateToDetectorReview} endIcon={OpenInNewIcon} size="small">
          Label image queries
        </GroundlightButton>
      )}
    </>
  );
}

// This component render the projected system accuracy and projected ml accuracy values. 
// this is displayed at the left side of the ProjectedAccuracy component.
function RenderAccuracyHeader({
  sysAccuracyValue,
  mlAccuracyValue,
  groundTruths,
  setOpenChartModal,
}: {
  setOpenChartModal: (value: boolean) => void;
  sysAccuracyValue: string;
  mlAccuracyValue: string;
  groundTruths: {
    yes: number;
    no: number;
  };
}) {
  const showLoading = useMemo(() => {
    const missingPassCount = Math.max(0, REQUIRED_GROUND_TRUTHS_PER_CLASS_IN_BINARY - (groundTruths?.yes || 0));
    const missingFailCount = Math.max(0, REQUIRED_GROUND_TRUTHS_PER_CLASS_IN_BINARY - (groundTruths?.no || 0));

    return missingPassCount + missingFailCount === 0 && !mlAccuracyValue;
  }, [groundTruths, mlAccuracyValue]);

  const { show_system_accuracy: showSysAcc } = useAppSelector(getUserState);

  return (
    <Box
      display="flex"
      flexDirection={{
        xs: "column",
        md: "row",
      }}
      columnGap={1}
      pl={2}
    >
      {showSysAcc && (
        <ProjectedAccuracyMetric
            metric="Projected System Accuracy"
            value={sysAccuracyValue}
            onInfo={setOpenChartModal}
            loading={showLoading}
          />
      )}
      <ProjectedAccuracyMetric
        metric="Projected ML Accuracy"
        value={mlAccuracyValue}
        onInfo={setOpenChartModal}
        loading={showLoading}
      />
    </Box>
  );
}

export interface ProjectedAccuracyProps {
  detectorData: Detector;
  totalImageQueries: number;
  lowestGroundTruthClassCount?: number;
}

// This is the main blue banner that displays all the information related to the projected accuracy.
export function ProjectedAccuracy(props: ProjectedAccuracyProps) {
  const { totalImageQueries, detectorData, lowestGroundTruthClassCount = 0 } = props;
  const [openChartModal, setOpenChartModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isPipelineDetailsUrl = location.pathname.endsWith("/pipelinedetails");
  const [openPipelineDetailsModal, setOpenPipelineDetailsModal] = useState(isPipelineDetailsUrl);
  const userState = useAppSelector(getUserState);
  const showPipelineDetailsModal = userState.show_pipeline_details_modal;

  useEffect(() => {
    if (isPipelineDetailsUrl && showPipelineDetailsModal) {
      setOpenPipelineDetailsModal(true);
    }
  }, [isPipelineDetailsUrl, showPipelineDetailsModal]);

  const handleClosePipelineDetails = () => {
    setOpenPipelineDetailsModal(false);
    if (isPipelineDetailsUrl) {
      navigate(`/reef/detectors/${detectorData.id}`, { replace: true });
    }
  };

  const handleOpenPipelineDetails = () => {
    setOpenPipelineDetailsModal(true);
    navigate(`/reef/detectors/${detectorData.id}/pipelinedetails`, { replace: true });
  };

  const mlAccuracy = detectorData?.evaluation_results?.kfold_pooled__balanced_accuracy || 0; // TODO: update to balanced_accuracy__lcb and update tooltip.
  const mlAccuracyValue = useMemo(() => {
    let accuracyValue = Number(mlAccuracy * 100);
    if (accuracyValue >= 99.99) {
      accuracyValue = 99.99;
    }
    return accuracyValue > 0 ? `${accuracyValue.toFixed(2)}%` : "";
  }, [mlAccuracy]);

  const sysAccuracyValue = useMemo(() => {
    let value = Number(
      (getAccuracyByThreshold(
        detectorData?.evaluation_results?.balanced_system_accuracies,
        detectorData?.confidence_threshold,
        false
      ) || 0) * 100
    );

    if (value >= 99.99) {
      value = 99.99;
    }
    return value ? `${value.toFixed(2)}%` : "";
  }, [detectorData?.evaluation_results?.balanced_system_accuracies, detectorData?.confidence_threshold]);

  return (
    <>
      <Box className="flex-wrap p-[10px] rounded-md w-full max-w-none bg-[#0450BA] flex flex-col md:flex-row justify-between gap-2">
        <RenderAccuracyHeader
          sysAccuracyValue={sysAccuracyValue}
          mlAccuracyValue={mlAccuracyValue}
          groundTruths={detectorData.ground_truths}
          setOpenChartModal={setOpenChartModal}
        />
        <Box
          sx={{
            justifyContent: "end",
            flex: 1,
            alignItems: { xs: "end", md: "center" },
            flexDirection: { xs: "column", md: "row" },
          }}
          className="flex flex-wrap gap-[5px] normal-case"
        >
          <RenderActions
            openAccuracyDetailsModal={() => setOpenChartModal(true)}
            totalQueries={totalImageQueries}
            groundTruths={detectorData.ground_truths}
            evaluationResults={detectorData?.evaluation_results}
            mlAccuracyTimestamp={detectorData?.evaluation_results?.eval_timestamp}
            detectorMode={detectorData.mode}
            classNames={detectorData.mode_configuration?.class_names ?? []}
            showPipelineDetailsModal={showPipelineDetailsModal}
            openPipelineDetailsModal={handleOpenPipelineDetails}
            lowestGroundTruthClassCount={lowestGroundTruthClassCount}
          />
        </Box>
      </Box>

      {openChartModal && (
        <ProjectedAccuracyModal
          handleTooltipModalClose={() => setOpenChartModal(false)}
          isTooltipModalOpen={openChartModal}
          detectorData={detectorData}
        />
      )}

      {showPipelineDetailsModal && openPipelineDetailsModal && (
        <PipelineDetailsModal
          isOpen={openPipelineDetailsModal}
          onClose={handleClosePipelineDetails}
          detectorId={detectorData.id}
        />
      )}
    </>
  );
}

interface SimpleProjectedAccuracyProps {
  detectorData?: Detector;
}

// This is a simplified version of the ProjectedAccuracy component. Used on modes that don't support projected accuracy.
export function SimpleProjectedAccuracy({ detectorData }: SimpleProjectedAccuracyProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const isPipelineDetailsUrl = location.pathname.endsWith("/pipelinedetails");
  const [openPipelineDetailsModal, setOpenPipelineDetailsModal] = useState(isPipelineDetailsUrl);
  const userState = useAppSelector(getUserState);
  const showPipelineDetailsModal = userState.show_pipeline_details_modal;

  useEffect(() => {
    if (isPipelineDetailsUrl && showPipelineDetailsModal && detectorData) {
      setOpenPipelineDetailsModal(true);
    }
  }, [isPipelineDetailsUrl, showPipelineDetailsModal, detectorData]);

  const handleClosePipelineDetails = () => {
    setOpenPipelineDetailsModal(false);
    if (isPipelineDetailsUrl && id) {
      navigate(`/reef/detectors/${id}`, { replace: true });
    }
  };

  const handleOpenPipelineDetails = () => {
    setOpenPipelineDetailsModal(true);
    if (id) {
      navigate(`/reef/detectors/${id}/pipelinedetails`, { replace: true });
    }
  };

  const navigateToDetectorReview = () => navigate(`/reef/review/queue?detector_id=${id}&ground_truth=true`);
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "end",
          gap: 2,
          px: "10px",
          width: "100%",
          height: "50px",
          borderRadius: "6px",
          bgcolor: "#0450BA",
        }}
      >
        <GroundlightButton onClick={navigateToDetectorReview} endIcon={OpenInNewIcon} size="small">
          Label image queries
        </GroundlightButton>
        {showPipelineDetailsModal && detectorData && (
          <GroundlightButton onClick={handleOpenPipelineDetails} endIcon={SettingsIcon} size="small">
            Pipeline Details
          </GroundlightButton>
        )}
      </Box>

      {showPipelineDetailsModal && detectorData && openPipelineDetailsModal && (
        <PipelineDetailsModal
          isOpen={openPipelineDetailsModal}
          onClose={handleClosePipelineDetails}
          detectorId={detectorData.id}
        />
      )}
    </>
  );
}
